import { useState, useEffect } from 'react';
import { css, jsx } from '@emotion/react';
import DayPicker from 'react-day-picker';
import { useClickOutside } from '../../../hooks/useClickOutside.es.js';
import '../../../theme/Colors.es.js';
import '../../../theme/Shadows.es.js';
import '../../../theme/Typography.es.js';
import { Zindexes } from '../../../theme/Zindexes.es.js';
import { Button } from '../../navigation/Button/Button.es.js';
import { ButtonNav } from '../../navigation/ButtonNav/ButtonNav.es.js';
import { FloatingBox } from '../../display/FloatingBox/FloatingBox.es.js';
import { DateRangePickerInput } from './DateRangePickerInput.es.js';
import { DateRangePickerNavbar } from './DateRangePickerNavbar.es.js';
import { WithDayPickerStyle } from './WithDayPickerStyle.es.js';
import { isSameDay, calculateNextRange } from './utils.es.js';
import { labelStyles } from '@zapier/style-encapsulation';

const Styles = labelStyles("DateRangePicker", {
  root: css`
    position: relative;
  `,
  menu: (props) => [
    css`
      display: grid;
      gap: var(--zds-space-20);
      padding: var(--zds-space-20);
    `,
    props.size === "large" && css`
        grid-template-columns: auto 1fr;
      `
  ],
  options: (props) => [
    css`
      display: flex;
      flex-direction: column;
      gap: var(--zds-space-12);
    `,
    props.size === "large" && css`
        border-right: var(--zds-border-weak);
        padding-right: var(--zds-space-20);
        min-width: 195px;
      `
  ],
  doneButton: css`
    display: flex;
    justify-content: flex-end;
    grid-column: 1 / -1;
  `,
  modalBackdrop: css`
    background: rgba(0, 0, 0, 0.8);
    position: fixed;
    z-index: ${Zindexes.modalOverlay};
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  modal: (size) => [
    css`
      background: var(--zds-background-weaker);
      border-radius: var(--zds-radius-large);
      overflow: auto;
      width: 100%;
      max-width: 344px;
      max-height: 100vh;
    `,
    size === "small" && css({
      maxWidth: "344px"
    }),
    size === "medium" && css({
      maxWidth: "670px"
    })
  ]
});
const defaultValue = {};
const DateRangePicker = ({ "aria-label": ariaLabel, menuAriaLabel, onChange, value: valueProp = defaultValue, placeholder = void 0, disabledDates = void 0, id = void 0, isDisabled = void 0, isErrored = void 0, closeText = "Done", options = [], menuAlign = "left", size = "medium", inputSize = "medium", variant: variantProp = void 0 }) => {
  const props = {
    "aria-label": ariaLabel,
    menuAriaLabel,
    onChange,
    value: valueProp,
    placeholder,
    disabledDates,
    id,
    isDisabled,
    isErrored,
    closeText,
    options,
    menuAlign,
    size
  };
  let variant = variantProp;
  if (!variant) {
    if (size === "small") {
      variant = "modal";
    } else {
      variant = "dropdown";
    }
  }
  const [selectedOption, setSelectedOption] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState(props.value || defaultValue);
  const { from, to } = value;
  const clampedSize = props.size === "small" ? props.size : "medium";
  const isSmall = props.size === "small";
  useEffect(() => {
    if (props.value !== value) {
      setValue(props.value);
    }
  }, [props.value]);
  const clearSelection = () => {
    setValue(defaultValue);
    props.onChange(defaultValue);
  };
  const closeMenu = () => {
    var _a, _b;
    setTimeout(() => {
      setIsOpen(false);
    }, 0);
    if (!isSameDay(from, (_a = props.value) === null || _a === void 0 ? void 0 : _a.from) || !isSameDay(to, (_b = props.value) === null || _b === void 0 ? void 0 : _b.to)) {
      props.onChange(value);
    }
  };
  const openMenu = () => {
    setIsOpen(true);
  };
  const handleDayClick = (day, modifiers, classNames) => {
    const disabledModifier = classNames.disabled;
    if (modifiers[disabledModifier]) {
      return;
    }
    const nextRange = calculateNextRange(day, from, to);
    setValue(nextRange);
    if (selectedOption !== null) {
      setSelectedOption(null);
    }
  };
  const handleSelectOption = (option, event) => {
    event.preventDefault();
    setSelectedOption(option);
    setValue(option.value);
  };
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: closeMenu
  });
  const menuInner = isOpen && jsx(
    "div",
    { "aria-label": props.menuAriaLabel, css: Styles.menu(props), "data-zds": true },
    props.options.length > 0 && jsx("div", { css: Styles.options(props), "data-zds": true }, props.options.map((option) => jsx(ButtonNav, { "aria-label": "Predefined date ranges", isButtonFullWidth: true, items: [option], onChooseItem: handleSelectOption, selectedItemKey: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.key }))),
    jsx(WithDayPickerStyle, { size: clampedSize }, ({ classNames, modifiersStyles }) => jsx(DayPicker, { classNames, disabledDays: props.disabledDates, fixedWeeks: true, initialMonth: from, modifiers: value, modifiersStyles, navbarElement: DateRangePickerNavbar, numberOfMonths: isSmall ? 1 : 2, onDayClick: (day, modifiers) => handleDayClick(day, modifiers, classNames), selectedDays: [from, value] })),
    jsx(
      "div",
      { css: Styles.doneButton, "data-zds": true },
      jsx(Button, { isFullWidth: props.size === "small", onClick: closeMenu }, props.closeText)
    )
  );
  return jsx(
    "div",
    { css: Styles.root, "data-zds": true },
    jsx(DateRangePickerInput, Object.assign({}, props, { clearSelection, isOpen, onClick: () => {
      if (!props.isDisabled && !isOpen) {
        openMenu();
      }
    }, options: props.options, value, size: inputSize })),
    isOpen && (variant === "modal" ? jsx(
      "div",
      { css: Styles.modalBackdrop, "data-zds": true },
      jsx("div", { css: Styles.modal(clampedSize), ref: setClickOutsideNode, "data-zds": true }, menuInner)
    ) : jsx(FloatingBox, { align: props.menuAlign, borderRadius: "large", intersectHeight: 460, onClickOutside: closeMenu }, menuInner))
  );
};

export { DateRangePicker };
//# sourceMappingURL=DateRangePicker.es.js.map
